import { z } from 'zod';

import { CashMovementKindSchema } from './cash-movement.schema.js';
import {
  CashDenominationSchema,
  CashSessionCloseModeSchema,
  CashSessionStatusSchema,
} from './cash-sessions.schema.js';
import {
  CashDailyForceUnverifiedAuditRiskRowDtoSchema,
  CashDailyPhysicalActivityTotalsDtoSchema,
  CashDailySummaryClosedSessionRowDtoSchema,
  CashDailySummaryOpenSessionRowDtoSchema,
} from './cash-report-daily.schema.js';
import type {
  CashSessionActivityCashActivityDto,
  CashSessionActivityCashPositionDto,
  CashSessionActivityMaterialBreakdownDto,
  CashSessionActivityPurchaseBreakdownDto,
  CashSessionActivitySummaryDto,
  CashSummaryMovementTotalsDto,
  DailyCashSummaryItemDto,
  DailyCashSummarySessionDto,
  GetCashSessionActivitySummaryRequest,
  GetCashSessionActivitySummaryResponse,
  GetDailyCashSummaryRequest,
  GetDailyCashSummaryResponse,
} from './cash-summary.dto.js';

const timestampSchema = z.iso.datetime();
const LocalDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected localDate in YYYY-MM-DD format');

export const CashSummaryMovementTotalsDtoSchema: z.ZodType<CashSummaryMovementTotalsDto> = z.object(
  {
    movementKind: CashMovementKindSchema,
    totalIn: z.number().int().nonnegative(),
    totalOut: z.number().int().nonnegative(),
  },
);

export const DailyCashSummarySessionDtoSchema: z.ZodType<DailyCashSummarySessionDto> = z.object({
  expectedCash: z.number().int().nullable(),
  countedCash: z.number().int().nullable(),
  overShort: z.number().int().nullable(),
  closeMode: CashSessionCloseModeSchema.nullable(),
  auditRisk: z.boolean(),
  closeDenominations: z.array(CashDenominationSchema).nullable().optional(),
});

export const DailyCashSummaryItemDtoSchema: z.ZodType<DailyCashSummaryItemDto> = z.object({
  localDate: LocalDateSchema,
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  timezone: z.string().min(1),
  sessionCount: z.number().int().nonnegative(),
  overShortTotal: z.number().int(),
  movementTotals: z.array(CashSummaryMovementTotalsDtoSchema),
  sessions: z.array(DailyCashSummarySessionDtoSchema),
  closedSessionRows: z.array(CashDailySummaryClosedSessionRowDtoSchema).optional(),
  openSessionRows: z.array(CashDailySummaryOpenSessionRowDtoSchema).optional(),
  physicalActivityTotals: z.array(CashDailyPhysicalActivityTotalsDtoSchema).optional(),
  forceUnverifiedAuditRiskRows: z.array(CashDailyForceUnverifiedAuditRiskRowDtoSchema).optional(),
});

export const GetDailyCashSummaryRequestSchema: z.ZodType<GetDailyCashSummaryRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid().optional(),
  locationId: z.uuid().optional(),
  from: timestampSchema.optional(),
  to: timestampSchema.optional(),
});

export const GetDailyCashSummaryResponseSchema: z.ZodType<GetDailyCashSummaryResponse> = z.object({
  items: z.array(DailyCashSummaryItemDtoSchema),
});

export const CashSessionActivityCashPositionDtoSchema: z.ZodType<CashSessionActivityCashPositionDto> =
  z.object({
    openingCashMinor: z.number().int(),
    cashInMinor: z.number().int().nonnegative(),
    cashOutMinor: z.number().int().nonnegative(),
    expectedCashMinor: z.number().int(),
    countedCashMinor: z.number().int().nullable(),
    overShortMinor: z.number().int().nullable(),
  });

export const CashSessionActivityCashActivityDtoSchema: z.ZodType<CashSessionActivityCashActivityDto> =
  z.object({
    purchasesPaidMinor: z.number().int().nonnegative(),
    purchaseReversalsMinor: z.number().int().nonnegative(),
    expensesMinor: z.number().int().nonnegative(),
    expenseVoidsMinor: z.number().int().nonnegative(),
    transfersOutMinor: z.number().int().nonnegative(),
    transfersInMinor: z.number().int().nonnegative(),
  });

export const CashSessionActivityMaterialBreakdownDtoSchema: z.ZodType<CashSessionActivityMaterialBreakdownDto> =
  z.object({
    materialName: z.string().trim().min(1).max(200),
    materialPayoutBeforeAdjustmentsMinor: z.number().int(),
    bonusesPromotionsMinor: z.number().int(),
    discountsMinor: z.number().int(),
    finalLinePayoutMinor: z.number().int(),
  });

export const CashSessionActivityPurchaseBreakdownDtoSchema: z.ZodType<CashSessionActivityPurchaseBreakdownDto> =
  z.object({
    materialPayoutBeforeAdjustmentsMinor: z.number().int(),
    bonusesPromotionsMinor: z.number().int(),
    discountsMinor: z.number().int(),
    roundingAdjustmentMinor: z.number().int(),
    finalPurchaseCashPaidMinor: z.number().int(),
    undetailedPurchaseCashPaidMinor: z.number().int(),
    materialBreakdown: z.array(CashSessionActivityMaterialBreakdownDtoSchema),
  });

export const CashSessionActivitySummaryDtoSchema: z.ZodType<CashSessionActivitySummaryDto> =
  z.object({
    cashSessionId: z.uuid(),
    status: CashSessionStatusSchema,
    cashPosition: CashSessionActivityCashPositionDtoSchema,
    cashActivity: CashSessionActivityCashActivityDtoSchema,
    purchaseBreakdown: CashSessionActivityPurchaseBreakdownDtoSchema,
  });

export const GetCashSessionActivitySummaryRequestSchema: z.ZodType<GetCashSessionActivitySummaryRequest> =
  z.object({
    companyId: z.uuid(),
    cashSessionId: z.uuid(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    stationId: z.uuid().optional(),
  });

export const GetCashSessionActivitySummaryResponseSchema: z.ZodType<GetCashSessionActivitySummaryResponse> =
  z.object({
    summary: CashSessionActivitySummaryDtoSchema,
  });
